<template>
  <PageLayoutWrapper page="preview/_id">
    <span class="block font-bold text-red-400 text-center text-xl mb-4">
      THIS IS A PAGE PREVIEW
    </span>
    <div v-if="hasTabs">
      <h1>{{ page.name }}</h1>
      <ul v-if="enableNonScrollableSubNavigation" class="flex flex-row flex-wrap px-4 md:px-8">
        <li
          v-for="tab in allTabs"
          :key="tab.id"
          class="-mb-px mr-1"
        >
          <nuxt-link
            v-if="selectedTab !== tab.anchor"
            :to="locationFromTab(tab)"
            class="bg-white inline-block py-2 px-2 md:px-4 text-blue-600 font-semibold">
               {{tab.name}}
          </nuxt-link>
          <span
            v-else
            class="current-tab bg-white inline-block py-2 px-2 md:px-4 text-blue-600 font-bold"
          >
            {{ tab.name }}
          </span>
        </li>
      </ul>
      <HorizontalScroll v-else class="mt-12 mb-8" :hide-arrows="allTabs.length <= 1">
        <ul class="flex w-full tab-list">
          <li
            v-for="tab in allTabs"
            :key="tab.id"
            class="mr-1 flex-shrink-0 w-auto text-center list-item"
          >
            <nuxt-link
              v-if="selectedTab !== tab.anchor"
              :to="locationFromTab(tab)"
              class="bg-white inline-block px-2 md:px-4 text-blue-600 font-semibold my-1"
            >
              {{ tab.name }}
            </nuxt-link>
            <span
              v-else
              class="current-tab bg-white inline-block px-2 md:px-4 text-blue-600 font-bold my-1"
            >
              {{ tab.name }}
            </span>
          </li>
        </ul>
      </HorizontalScroll>
    </div>
    <RichContent :content="pageContent" />
  </PageLayoutWrapper>
</template>

<script>
import CustomPage from '../pages/_id'
import { NON_SCROLLABLE_SUB_NAVIGATION } from '@/constants/featureFlags'
import useFeatureFlags from '@/composables/useFeatureFlags'
import HorizontalScroll from '@/components/HorizontalScroll'
import PageLayoutWrapper from '@/components/PageLayoutWrapper'
import RichContent from '@/components/RichContent'
import pageLayout from '@/mixins/pageLayoutMixin'
import useMeta from '@/composables/useMeta'

export default {
  components: { PageLayoutWrapper, RichContent, HorizontalScroll },
  extends: CustomPage,
  mixins: [pageLayout],
  head() {
    return {
      title: `${this.page.name} | ${this.catalogWideMetaTitle}`,
      meta: [
        {
          hid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
        {
          hid: 'description',
          name: 'description',
          content: this.page.name,
        },
      ],
    }
  },
  setup() {
    const { enableNonScrollableSubNavigation } = useFeatureFlags({
      enableNonScrollableSubNavigation: NON_SCROLLABLE_SUB_NAVIGATION,
    })
    const { catalogWideMetaTitle } = useMeta()
    return { enableNonScrollableSubNavigation, catalogWideMetaTitle }
  },
  computed: {
    hasWidgets() {
      return !!(
        this.page &&
        ((this.page.widgets && this.page.widgets.length) ||
          (this.page.temporaryWidgets && this.page.temporaryWidgets.length))
      )
    },
    tabs() {
      if (this.hasWidgets) {
        const tabWidget = this.page.widgets.find(
          (widget) => widget.type === 'tabs'
        )
        const tempTabWidget =
          this.page.temporaryWidgets &&
          this.page.temporaryWidgets.find((widget) => widget.type === 'tabs')

        return (
          (tempTabWidget && tempTabWidget.data) ||
          (tabWidget && tabWidget.data) ||
          []
        )
      }
      return []
    },

    pageContent() {
      if (!this.page) return
      return (
        (this.currentTab && this.currentTab.content) ||
        this.page.temporaryContent ||
        this.page.content
      ).replace(/data-colwidth/, 'width') // for tables with a fixed columns width, we need to change attribute data-colwidth to width
    },
  },
  async asyncData({ params, $bugsnag, $axios, redirect, store }) {
    const id = params.id
    const url = `/ca/${store.state.settings.school}/catalogPages/${
      store.state.settings.activeCatalog
    }/${id}`
    try {
      const page = await $axios.$get(url)
      return {
        page,
      }
    } catch (e) {
      $bugsnag.notify(e)
      redirect('/404')
    }
  },
}
</script>


<style lang="css">
span:first-child
  + h1
  + div
  > div:first-child#content
  > h1:first-child.page-title {
  display: none;
}

.tab-list {
  margin-bottom: 0 !important;
}

.list-item {
  /* limiting width of list item to 85% so that longer text is fully visible
  within the viewport specially on mobile screen */
  max-width: 85%;
}
</style>
